import type { QuoteAttachment } from '@/utils/quote_attachment_utils';

export interface EstimatorSiteHistoryFile {
    id: number;
    title: string;
    web_url?: string | null;
    section?: string | null;
    filename?: string | null;
    content_type?: string | null;
    /** Quote-drive relative path — same as QuoteDriveExplorer preview. */
    relative_path?: string | null;
}

export interface EstimatorSiteHistoryQuote {
    id: number;
    folder_number: number | null;
    status: number;
    status_label: string;
    is_legacy: boolean;
    created_at: string;
    total_area: number;
    total_price: number;
    type_of_work?: string | null;
    roof_type?: string | null;
    desired_steep_slope?: string | null;
    desired_low_slope?: string | null;
    file_count: number;
    files: EstimatorSiteHistoryFile[];
    project_id?: number | null;
    project_phase?: string | null;
    project_phase_label?: string | null;
}

export interface EstimatorProjectByAddress {
    id: number;
    project_no: number | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    folio_number: string | null;
    contractor_id?: number | null;
    property_owner_id?: number | null;
    client_name: string;
    contractor_name?: string;
    email: string;
    phone: string;
    year_built: number | null;
    type: string;
    phase: string;
    quotes?: EstimatorSiteHistoryQuote[];
}

/** Projects that exist only for legacy import — hide from project picker. */
export function isLegacyOnlyProject(row: EstimatorProjectByAddress): boolean {
    const quotes = row.quotes ?? [];
    return quotes.length > 0 && quotes.every(q => q.is_legacy);
}

export function selectableProjectsByAddress(
    rows: EstimatorProjectByAddress[],
): EstimatorProjectByAddress[] {
    return rows.filter(row => !isLegacyOnlyProject(row));
}

function phaseLabel(phase: string): string {
    if (phase === 'active') return 'Active';
    if (phase === 'quoting') return 'Pending';
    return phase;
}

/**
 * Flatten quotes for site history. Drops duplicate quote ids and near-identical
 * legacy pending clones (same price/area/status) that appear from re-imports.
 */
export function aggregateSiteHistoryQuotes(
    rows: EstimatorProjectByAddress[],
): EstimatorSiteHistoryQuote[] {
    const quotes = rows.flatMap(row =>
        (row.quotes ?? []).map(q => ({
            ...q,
            project_id: q.project_id ?? row.id,
            project_phase: q.project_phase ?? row.phase,
            project_phase_label: q.project_phase_label ?? phaseLabel(row.phase),
        })),
    );
    const sorted = [...quotes].sort((a, b) => {
        const ta = a.created_at || '';
        const tb = b.created_at || '';
        return tb.localeCompare(ta);
    });
    const seenIds = new Set<number>();
    const seenFingerprints = new Set<string>();
    const out: EstimatorSiteHistoryQuote[] = [];
    for (const quote of sorted) {
        if (seenIds.has(quote.id)) continue;
        seenIds.add(quote.id);
        const fingerprint = quote.is_legacy
            ? [
                  'legacy',
                  quote.status,
                  quote.total_area,
                  quote.total_price,
                  quote.type_of_work ?? '',
                  quote.roof_type ?? '',
              ].join('|')
            : [
                  'modern',
                  quote.project_id ?? '',
                  quote.folder_number ?? '',
                  quote.status,
                  quote.total_area,
                  quote.total_price,
              ].join('|');
        if (seenFingerprints.has(fingerprint)) continue;
        seenFingerprints.add(fingerprint);
        out.push(quote);
    }
    return out;
}

function guessHistoryContentType(filename: string): string {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (/\.jpe?g$/.test(lower)) return 'image/jpeg';
    return 'application/octet-stream';
}

export function isRelatedOrViewsSection(section: string | null | undefined): boolean {
    return section === 'related' || section === 'views';
}

/** Same preview shape QuoteDriveExplorer uses when relative_path is known. */
export function projectFileToHistoryAttachment(
    file: EstimatorSiteHistoryFile,
    quoteId: number,
): QuoteAttachment {
    const filename = file.filename || file.title || `file-${file.id}`;
    const content_type = file.content_type || guessHistoryContentType(filename);
    if (file.relative_path) {
        return {
            id: file.relative_path,
            name: file.title || filename,
            filename,
            content_type,
            source: 'drive',
            quote_id: quoteId,
            drive_path: file.relative_path,
        };
    }
    return {
        id: String(file.id),
        project_file_id: file.id,
        name: file.title || filename,
        filename,
        content_type,
        source: 'document',
    };
}

/**
 * Prefer ProjectFile / drive refs over stale local attachment UUIDs in
 * estimation_data so address-history preview matches Quote drive explorer.
 */
export function resolveSiteHistoryAttachments(
    quoteId: number,
    files: EstimatorSiteHistoryFile[],
    fromEstimation: QuoteAttachment[],
): QuoteAttachment[] {
    const sectioned = files.filter(f => isRelatedOrViewsSection(f.section));
    const byFilename = new Map(
        sectioned.map(f => [(f.filename || f.title || '').toLowerCase(), f]),
    );

    if (fromEstimation.length === 0) {
        return sectioned.map(f => projectFileToHistoryAttachment(f, quoteId));
    }

    return fromEstimation.map(att => {
        if (att.source === 'drive' && att.quote_id != null && att.drive_path) return att;
        const matchById =
            att.project_file_id != null
                ? sectioned.find(f => f.id === att.project_file_id)
                : undefined;
        const match =
            matchById ||
            byFilename.get(att.filename.toLowerCase()) ||
            byFilename.get(att.name.toLowerCase());
        if (match) return projectFileToHistoryAttachment(match, quoteId);
        return att;
    });
}
